import { useRef, type PointerEvent } from 'react';

/** Quanto o dedo precisa ficar parado para virar toque longo (o padrão do Android é 400–500ms). */
const ESPERA_MS = 500;
/** Mexer mais que isto é rolar a lista, não pressionar. */
const TOLERANCIA_PX = 10;

/**
 * Toque longo num elemento que também tem clique (o cartão do processo no celular: o toque
 * abre os autos, o toque longo entra no modo seleção — docs/PLANO_RESPONSIVIDADE_MOBILE.md §3.5).
 *
 * Devolve os handlers de ponteiro e `consumiuClique()`: o `click` que o navegador dispara ao
 * soltar o dedo depois de um toque longo não deve abrir nada — o onClick pergunta antes.
 * Só vale para toque (pointerType `touch`); no mouse, o clique direito é do navegador. Não
 * começa num controle de dentro (botão, link, item de menu) nem em algo que só chegou pela
 * árvore do React vindo de um portal (o menu ⋮ aberto, o popover de tags): ali o dedo é do
 * controle. Com `habilitado` falso, não vibra nem segura o clique — o toque abre como sempre.
 */
export function useToqueLongo(aoPressionar: () => void, habilitado = true) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inicio = useRef<{ x: number; y: number } | null>(null);
  const disparou = useRef(false);

  const cancelar = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    inicio.current = null;
  };

  return {
    handlers: {
      onPointerDown: (e: PointerEvent) => {
        if (!habilitado || e.pointerType !== 'touch') return;
        const alvo = e.target as Element;
        if (!e.currentTarget.contains(alvo) || alvo.closest('button, a, input, [role="menuitem"], [role="checkbox"]')) return;
        disparou.current = false;
        inicio.current = { x: e.clientX, y: e.clientY };
        timer.current = setTimeout(() => {
          disparou.current = true;
          timer.current = null;
          navigator.vibrate?.(15);
          aoPressionar();
        }, ESPERA_MS);
      },
      onPointerMove: (e: PointerEvent) => {
        const p = inicio.current;
        if (p && Math.hypot(e.clientX - p.x, e.clientY - p.y) > TOLERANCIA_PX) cancelar();
      },
      onPointerUp: cancelar,
      onPointerCancel: cancelar,
      onPointerLeave: cancelar,
      // O menu de contexto do celular (copiar link, selecionar texto) briga com o gesto.
      onContextMenu: (e: { preventDefault: () => void }) => {
        if (disparou.current || timer.current) e.preventDefault();
      },
    },
    /** `true` uma vez depois de um toque longo: o clique que vem junto deve ser ignorado. */
    consumiuClique: () => {
      const sim = disparou.current;
      disparou.current = false;
      return sim;
    },
  };
}
